import { DocumentViewer } from '@aecom-ai-engineering/react-components';
import {
  Button,
  CustomSelect,
  DEFAULT_PAGE_SIZE,
  LoadingArea,
  Pagination,
  Search,
  type SelectOption,
  Spinner,
} from '@consigli/facade';
import {
  useBlobs,
  useDebouncedSearch,
  useDownloadFilesAsZip,
  useGetBlobNameListQuery,
  useGetBlobReferenceCountQuery,
  useLazyGetBlobsQuery,
  usePackageId,
  useProjectId,
} from '@consigli/hooks';
import {
  type Blob,
  BlobParsedStatus,
  convertBlobToDocument,
  OTHER_REFERENCE,
} from '@consigli/types';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Highlight } from 'react-highlighter-ts';
import { useTranslation } from 'react-i18next';
import { MdOutlineFileDownload } from 'react-icons/md';

import { Card } from '@/components/card';
import { DownloadFileButton } from '@/components/download-file-button';
import { Breadcrumb } from '@/features/breadcrumb/breadcrumb';
import { DocumentCardParsedData } from '@/features/document-list/document-card-parsed-data';
import { createRenderers } from '@/features/document-viewer/renderers';
import { WorkspaceProvider } from '@/layouts/workspace-layout/use-workspace-context';
import { WorkspaceLayout } from '@/layouts/workspace-layout/workspace-layout';
import {
  mapExtensionToUppercase,
  resolveDocumentFileType,
  resolveDocumentSrc,
} from '@/util/document-check';
import { snapCenterToCursor } from '@/util/dragging-util';

import { BID_LEVELLING_REFERENCE, PRIMARY_REFERENCE, blobReference } from './offer-references';

const ALL_TAB = 'all';

// The statuses a bid-review document can be in; shared by the list, the tab
// counts, and the download so all three agree on what "a document" is.
const DOCUMENT_STATUSES = [
  BlobParsedStatus.UPLOADING,
  BlobParsedStatus.UPLOADED,
  BlobParsedStatus.UNPARSED,
  BlobParsedStatus.PARSED,
  BlobParsedStatus.PARSING_FAILED,
];

interface DocumentTab {
  key: string;
  label: string;
  count: number;
}

const OfferDocumentsContent: FC = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const [activeTab, setActiveTab] = useState<string>(ALL_TAB);
  const [page, setPage] = useState(1);
  const [activeBlobId, setActiveBlobId] = useState<string | null>(null);
  const [searchContent, setSearchContent] = useState('');
  const [selectedBlobIds, setSelectedBlobIds] = useState<string[]>([]);
  const debouncedSearchContent = useDebouncedSearch(searchContent, 500);
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(KeyboardSensor),
    useSensor(PointerSensor),
  );

  const { data: referenceCounts = {}, isLoading: isLoadingCounts } = useGetBlobReferenceCountQuery({
    projectId,
    packageId,
    status: DOCUMENT_STATUSES,
  });

  const referenceLabel = useCallback(
    (reference: string | undefined): string => {
      if (reference === PRIMARY_REFERENCE) return t('bid-review.documents.tender');
      if (reference === BID_LEVELLING_REFERENCE) return t('bid-review.documents.leveling');
      if (reference) return reference;
      return t('bid-review.documents.other');
    },
    [t],
  );

  const tabs = useMemo<DocumentTab[]>(() => {
    const totalCount = Object.values(referenceCounts).reduce((total, count) => total + count, 0);
    const result: DocumentTab[] = [
      { key: ALL_TAB, label: t('bid-review.documents.tab-all'), count: totalCount },
    ];
    if (referenceCounts[PRIMARY_REFERENCE]) {
      result.push({
        key: PRIMARY_REFERENCE,
        label: t('bid-review.documents.tender'),
        count: referenceCounts[PRIMARY_REFERENCE],
      });
    }
    const contractorNames = Object.keys(referenceCounts)
      .filter(
        (reference) =>
          reference !== PRIMARY_REFERENCE &&
          reference !== BID_LEVELLING_REFERENCE &&
          reference !== OTHER_REFERENCE,
      )
      .sort((a, b) => a.localeCompare(b));
    for (const name of contractorNames) {
      result.push({ key: name, label: name, count: referenceCounts[name] });
    }
    if (referenceCounts[BID_LEVELLING_REFERENCE]) {
      result.push({
        key: BID_LEVELLING_REFERENCE,
        label: t('bid-review.documents.leveling'),
        count: referenceCounts[BID_LEVELLING_REFERENCE],
      });
    }
    if (referenceCounts[OTHER_REFERENCE]) {
      result.push({
        key: OTHER_REFERENCE,
        label: t('bid-review.documents.other'),
        count: referenceCounts[OTHER_REFERENCE],
      });
    }
    return result;
  }, [referenceCounts, t]);

  const activeReference = activeTab === ALL_TAB ? undefined : activeTab;

  const activeSearchContent = debouncedSearchContent.length >= 3 ? debouncedSearchContent : '';

  const { blobs, paginatedCount, isFetching } = useBlobs({
    projectId,
    packageId,
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    status: DOCUMENT_STATUSES,
    reference: activeReference,
    searchParsedData: activeSearchContent,
    blobIds: selectedBlobIds.length > 0 ? selectedBlobIds : undefined,
  });

  const { data: blobNameList } = useGetBlobNameListQuery({
    projectId,
    packageId,
    searchParsedData: activeSearchContent,
    status: DOCUMENT_STATUSES,
  });

  const blobNameOptions: SelectOption[] = useMemo(
    () => (blobNameList ?? []).map((spec) => ({ label: spec.name, value: spec.id })),
    [blobNameList],
  );

  const handleTabChange = useCallback((key: string) => {
    setActiveTab(key);
    setPage(1);
  }, []);

  const handleSearchContent = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchContent(event.target.value);
    setPage(1);
  }, []);

  const handleFilterBlobNames = useCallback((options: SelectOption[]) => {
    setSelectedBlobIds(options.length > 0 ? options.map((option) => option.value.toString()) : []);
    setPage(1);
  }, []);

  useEffect(() => {
    if (isFetching) return;
    const maxPage = Math.max(1, Math.ceil(paginatedCount / DEFAULT_PAGE_SIZE));
    if (page > maxPage) setPage(maxPage);
  }, [isFetching, paginatedCount, page]);

  const { isDownloading, downloadFileZip } = useDownloadFilesAsZip('bid-review-documents');
  const [fetchAllBlobs, { isFetching: isFetchingAll }] = useLazyGetBlobsQuery();

  const handleDownload = useCallback(async () => {
    const response = await fetchAllBlobs({
      projectId,
      packageId,
      page: 'all',
      status: DOCUMENT_STATUSES,
      reference: activeReference,
      searchParsedData: activeSearchContent,
      blobIds: selectedBlobIds.length > 0 ? selectedBlobIds : undefined,
    }).unwrap();
    await downloadFileZip(response.results.map(convertBlobToDocument));
  }, [
    fetchAllBlobs,
    projectId,
    packageId,
    activeReference,
    activeSearchContent,
    selectedBlobIds,
    downloadFileZip,
  ]);

  const renderers = useMemo(() => createRenderers(), []);

  const activeBlobIndex = blobs.findIndex((blob) => blob.id === activeBlobId);
  const activeBlob = activeBlobIndex >= 0 ? blobs[activeBlobIndex] : null;

  const handleBlobClick = useCallback((blob: Blob) => {
    setActiveBlobId(blob.id);
  }, []);

  if (isLoadingCounts) {
    return <LoadingArea title={t('document-list.loading')} />;
  }

  return (
    <DndContext sensors={sensors} modifiers={[snapCenterToCursor, restrictToParentElement]}>
      <div className="w-full min-h-full bg-day-light-3 px-4 pb-4">
        <div className="flex items-center justify-between py-4">
          <span className="text-sm font-semibold text-day-neutral-dark">
            {t('document-list.total_documents')} ({paginatedCount})
          </span>
          <Button
            rounded
            secondary
            disabled={paginatedCount === 0 || isDownloading || isFetchingAll}
            onClick={handleDownload}
            icon={
              isDownloading || isFetchingAll
                ? () => <Spinner size="xsmall" />
                : () => <MdOutlineFileDownload />
            }
            className="text-sm font-bold min-h-[36px] whitespace-normal"
          >
            {t('bid-review.documents.download')}
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 pb-4">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => handleTabChange(tab.key)}
              className={
                activeTab === tab.key
                  ? 'rounded-full bg-day-neutral-dark px-3 py-1 text-xs font-medium text-white transition-colors'
                  : 'rounded-full bg-day-light-3 px-3 py-1 text-xs font-medium text-day-neutral-subtle transition-colors hover:bg-day-light-4'
              }
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        <div className="mb-4 flex w-full gap-x-6">
          <div className="z-20 w-1/2">
            <CustomSelect
              onChange={(selectedOptions: SelectOption[]): void => {
                handleFilterBlobNames(selectedOptions);
              }}
              isMulti
              isClearable
              placeholder={t('findings.search-document-names')}
              options={blobNameOptions}
              value={
                selectedBlobIds.length > 0
                  ? blobNameOptions.filter((option) =>
                      selectedBlobIds.includes(option.value as string),
                    )
                  : []
              }
            />
          </div>
          <div className="w-1/2">
            <Search
              text={searchContent}
              onChange={handleSearchContent}
              placeholder={t('findings.search_content')}
              className="w-full rounded bg-white"
            />
          </div>
        </div>

        <div className="overflow-auto">
          {isFetching ? (
            <Spinner />
          ) : blobs.length === 0 ? (
            <div className="bg-day-light-3 w-full flex flex-col items-center justify-center py-10">
              <div className="text-day-neutral-subtle text-xl font-medium">
                {t('bid-review.documents.no-documents')}
              </div>
            </div>
          ) : (
            blobs.map((blob) => {
              const document = convertBlobToDocument(blob);
              const selected = activeBlobId === blob.id;
              return (
                <div key={blob.id} className="flex items-center">
                  <div className="flex-1 ml-1">
                    <Card selected={selected} onClick={() => handleBlobClick(blob)}>
                      <div className="grid grid-cols-5 gap-x-4">
                        <div className="col-span-4 flex flex-col truncate">
                          <div
                            className={`text-xs mb-1 ${selected ? 'text-white' : 'text-day-neutral-subtle'}`}
                          >
                            {mapExtensionToUppercase(blob.extension)}
                          </div>
                          <div
                            className={`text-base font-semibold ${selected ? 'text-white' : 'text-day-neutral-dark'}`}
                          >
                            {/* @ts-expect-error - As event handlers which don't exist for React are required */}
                            <Highlight placeholder="" search={activeSearchContent}>
                              {blob.name}
                            </Highlight>
                          </div>
                          <div className="mt-1">
                            <span className="inline-block rounded-full bg-day-light-3 px-3 py-0.5 text-xs font-medium text-day-neutral-subtle">
                              {referenceLabel(blobReference(blob))}
                            </span>
                          </div>
                          <DocumentCardParsedData
                            initialParsedData={blob.parsedData.data}
                            remainingOccurrences={blob.parsedData.remainingOccurrences}
                            documentId={blob.id}
                            searchText={activeSearchContent}
                            selected={selected}
                          />
                        </div>
                        <div className="col-span-1 ml-auto flex items-center">
                          <DownloadFileButton
                            document={document}
                            selected={selected}
                            className="rounded-full"
                          />
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {paginatedCount > DEFAULT_PAGE_SIZE && (
          <div className="flex justify-center pt-4 text-day-neutral-dark">
            <Pagination
              currentPage={page}
              totalCount={paginatedCount}
              pageSize={DEFAULT_PAGE_SIZE}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
      {activeBlob && (
        <DocumentViewer
          open={activeBlobIndex >= 0}
          onOpenChange={(details) => {
            if (!details.open) setActiveBlobId(null);
          }}
          src={resolveDocumentSrc(activeBlob)}
          fileType={resolveDocumentFileType(activeBlob)}
          fileName={activeBlob.name}
          title={activeBlob.name}
          showSidebar={false}
          showNavigationActions={blobs.length > 1}
          previousDisabled={activeBlobIndex <= 0}
          nextDisabled={activeBlobIndex >= blobs.length - 1}
          onPrevious={() => setActiveBlobId(blobs[activeBlobIndex - 1]?.id ?? null)}
          onNext={() => setActiveBlobId(blobs[activeBlobIndex + 1]?.id ?? null)}
          renderers={renderers}
        />
      )}
    </DndContext>
  );
};

export const OfferDocumentsPage: FC = () => {
  const { t } = useTranslation();

  return (
    <WorkspaceProvider>
      <WorkspaceLayout>
        <Breadcrumb label={t('servicetabs.documents')} />
        <OfferDocumentsContent />
      </WorkspaceLayout>
    </WorkspaceProvider>
  );
};
